<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center mb-4">
          <h1  v-if="currentVendor" class="text-h3">{{ currentVendor.vendor }} unsubmitted orders</h1>
        </div>

        <!-- Error Alert -->
        <v-alert
          v-if="error"
          type="error"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <!-- Loading State -->
        <v-card v-if="loading" class="pa-4 text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <div class="text-h6 mt-4">Loading orders...</div>
        </v-card>

        <!-- No Orders State -->
        <v-card 
          v-else-if="!loading && orders.length === 0" 
          class="pa-4 text-center"
        >
          <v-icon
            icon="mdi-package-variant"
            size="64"
            color="grey"
          ></v-icon>
          <div class="text-h6 mt-4">No unsubmitted orders found</div>
          <div class="text-body-1 text-grey mt-2">
            There are currently no orders to process for {{ currentVendor?.vendor }}
          </div>
        </v-card>

        <!-- Order Wizard -->
        <order-wizard
          v-else
          :key="wizardKey"
          :orders="orders"
          :loading="loading"
          :current-page="currentPage"
          :total-pages="totalPages"
          :total-items="totalItems"
          :vendor="currentVendor"
          @page-changed="handlePageChange"
          @update-error="handleUpdateError"
          @reload-orders="reloadOrders"
          @order-assigned="handleOrderAssigned"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'OrderPage'
}
</script>

<script setup>
import { useVendorsStore } from '@/stores/vendorsStore'
import { vendorsService } from '@/services/vendorsService'
import { useRoute, useRouter } from 'vue-router'
import { onMounted, computed, ref, watch } from 'vue'
import { OrderWizard } from '@/components/orders'

const route = useRoute()
const router = useRouter()
const vendorsStore = useVendorsStore()

// State
const orders = ref([])
const loading = ref(false)
const error = ref(null)
const currentPage = ref(1)
const totalItems = ref(0)
const totalPages = ref(1)
const wizardKey = ref(0)

// Get vendor from route parameter
const currentVendor = computed(() => {
  return vendorsStore.vendors.find(v => v.slug === route.params.vendor)
})

// Fetch orders for the current vendor and page
const fetchOrders = async (page = 1) => {
  if (!currentVendor.value) return

  loading.value = true
  error.value = null

  try {
    const response = await vendorsService.getVendorSales(currentVendor.value.slug, page)
    orders.value = response.data
    totalPages.value = response.last_page
    currentPage.value = response.current_page
    totalItems.value = response.total
  } catch (err) {
    error.value = 'Failed to load orders'
    console.error('Error fetching orders:', err)
  } finally {
    loading.value = false
  }
}

const handleOrderAssigned = async (orderIds) => {
  const ordersToRemove = Array.isArray(orderIds) ? orderIds : [orderIds]
  orders.value = orders.value.filter(order => !ordersToRemove.includes(order.ID))
  try {
    const response = await vendorsService.getVendorSales(currentVendor.value.slug, currentPage.value)
    orders.value = response.data
    totalPages.value = response.last_page
    currentPage.value = response.current_page
  } catch (err) {
    console.error('Error fetching orders:', err)
  }
}

// Handle page changes
const handlePageChange = (page) => {
  router.push({
    query: { ...route.query, page }
  })
}

// Watch for page changes in URL
watch(
  () => route.query.page,
  (newPage) => {
    if (newPage) {
      fetchOrders(Number(newPage))
    }
  }
)

// Watch for vendor changes
watch(
  () => route.params.vendor,
  () => {
    currentPage.value = 1
    fetchOrders(1)
  }
)

// Initial setup
onMounted(async () => {
  await vendorsStore.fetchVendors()
  if (route.query.page) {
    fetchOrders(Number(route.query.page))
  } else {
    fetchOrders(1)
  }
})

const handleUpdateError = (error) => {
  console.error('Update error:', error)
  // Add error handling logic
}

const reloadOrders = () => {
  wizardKey.value++ // Force re-render of OrderWizard
  fetchOrders(1)
}
</script> 